import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

interface OrderInfoModalProps {
  isOpen: boolean;
  onSubmit: (orderData: any) => void;
  initialData?: {
    name: string;
    email: string;
    phone: string;
  };
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass = (hasError: boolean) =>
  `w-full border ${hasError ? 'border-red-500' : 'border-black'} rounded-none h-10 px-3`;

function Field({
  label,
  name,
  type = 'text',
  value,
  error,
  onChange,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  error?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div>
      <label className="block text-sm mb-1 italic">{label} *</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className={inputClass(!!error)}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export default function OrderInfoModal({ isOpen, onSubmit, initialData }: OrderInfoModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    firstSurname: '',
    secondSurname: '',
    email: initialData?.email || '',
    confirmEmail: '',
    phone: initialData?.phone || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const e: Record<string, string> = {};

    if (!formData.firstName.trim()) e.firstName = 'El nombre es requerido';
    if (!formData.firstSurname.trim()) e.firstSurname = 'El primer apellido es requerido';
    if (!formData.secondSurname.trim()) e.secondSurname = 'El segundo apellido es requerido';

    if (!formData.email.trim()) {
      e.email = 'El email es requerido';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      e.email = 'Email inválido';
    }

    if (!formData.confirmEmail.trim()) {
      e.confirmEmail = 'Confirma tu email';
    } else if (formData.email !== formData.confirmEmail) {
      e.confirmEmail = 'Los emails no coinciden';
    }

    if (!formData.phone.trim()) {
      e.phone = 'El teléfono es requerido';
    } else if (formData.phone.length < 9) {
      e.phone = 'Número de teléfono inválido';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const fullName = `${formData.firstName.trim()} ${formData.firstSurname.trim()} ${formData.secondSurname.trim()}`;
      await onSubmit({ name: fullName, email: formData.email, phone: formData.phone });
    } catch (error: any) {
      setErrors({ submit: error.message || 'Error al procesar el formulario' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePhoneChange = (value: string) => {
    const phone = value.startsWith('+') ? value : `+${value}`;
    setFormData(prev => ({ ...prev, phone }));
    if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
  };

  return (
    <Dialog open={isOpen} onClose={() => {}} className="relative z-[99999]">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg bg-white rounded-lg shadow-xl">
          <div className="p-5">
            <Dialog.Title className="text-xl font-bold mb-3">
              Información del Pedido
            </Dialog.Title>

            <p className="text-gray-600 text-sm mb-4">
              Para continuar con tu pedido, necesitamos algunos datos básicos.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <Field label="Nombre" name="firstName" value={formData.firstName} error={errors.firstName} onChange={handleChange} />

              <div className="grid grid-cols-2 gap-3">
                <Field label="Primer apellido" name="firstSurname" value={formData.firstSurname} error={errors.firstSurname} onChange={handleChange} />
                <Field label="Segundo apellido" name="secondSurname" value={formData.secondSurname} error={errors.secondSurname} onChange={handleChange} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Email" name="email" type="email" value={formData.email} error={errors.email} onChange={handleChange} />
                <Field label="Confirmar Email" name="confirmEmail" type="email" value={formData.confirmEmail} error={errors.confirmEmail} onChange={handleChange} />
              </div>

              <div>
                <label className="block text-sm mb-1 italic">Teléfono *</label>
                <div className="phone-input-container">
                  <PhoneInput
                    country={'es'}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    containerClass={errors.phone ? 'react-tel-input error' : 'react-tel-input'}
                    enableSearch
                    autoFormat
                    countryCodeEditable={false}
                    specialLabel=""
                    searchPlaceholder="Buscar país..."
                    searchNotFound="País no encontrado"
                    preferredCountries={['es', 'it', 'fr', 'pt', 'de', 'gb']}
                    inputProps={{ name: 'phone', required: true, placeholder: 'Número de teléfono' }}
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-500 text-red-500 p-3 rounded text-sm">
                  {errors.submit}
                </div>
              )}

              <div className="flex justify-end pt-1">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-[#F90000] text-white text-sm font-medium hover:bg-[#D80000] disabled:opacity-50"
                >
                  {isSubmitting ? 'Guardando...' : 'Continuar'}
                </button>
              </div>
            </form>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
